import React, { useState, useMemo, useEffect, useCallback } from 'react';
// @ts-ignore
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine, ResponsiveContainer } from 'recharts';
import { ClientData } from '../types/ClientData';
import { formatCurrency } from '../utils/format';
import { useCommaInput } from '../utils/useCommaInput';
import {
  mortgagePayoffMonths,
  totalInterestSaved,
  calcMortgagePayment,
  fvContributions,
} from '../utils/planCalculations';
import { getTaxYearForDate, TAX_YEARS } from '../taxConfig';

// ── Mortgage chart data generator ──────────────────────────────────────────
function generateMortgageChartData(
  balance: number,
  annualRate: number,
  termMonths: number,
  overpaymentMonthly: number,
  annualLumpSum: number,
  clientAge: number,
): Array<{ age: number; standard: number; withOverpayment: number }> {
  const data: Array<{ age: number; standard: number; withOverpayment: number }> = [];
  const r = annualRate / 100 / 12;
  const standardPayment = calcMortgagePayment(balance, annualRate, termMonths);

  let standardBal = balance;
  let opBal = balance;
  const years = Math.ceil(termMonths / 12) + 2;

  for (let year = 0; year <= years; year++) {
    data.push({
      age: clientAge + year,
      standard: Math.max(0, Math.round(standardBal)),
      withOverpayment: Math.max(0, Math.round(opBal)),
    });

    for (let m = 0; m < 12; m++) {
      if (standardBal > 0) standardBal = standardBal * (1 + r) - standardPayment;
    }

    for (let m = 0; m < 12; m++) {
      if (opBal > 0) opBal = opBal * (1 + r) - (standardPayment + overpaymentMonthly);
    }
    if (opBal > 0 && annualLumpSum > 0) opBal = Math.max(0, opBal - annualLumpSum);
  }

  return data;
}

// ── Binary search: find overpayment to achieve target payoff age ──────────
function overpaymentForTargetAge(
  balance: number,
  annualRate: number,
  termMonths: number,
  annualLumpSum: number,
  clientAge: number,
  targetAge: number,
): number {
  const targetMonths = Math.max(1, Math.round((targetAge - clientAge) * 12));
  let lo = 0;
  let hi = balance; // upper bound
  for (let i = 0; i < 60; i++) {
    const mid = (lo + hi) / 2;
    const months = mortgagePayoffMonths(balance, annualRate, termMonths, mid, annualLumpSum);
    if (months <= targetMonths) {
      hi = mid;
    } else {
      lo = mid;
    }
    if (hi - lo < 0.5) break;
  }
  return Math.ceil(hi);
}

interface PlanMortgageProps {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

export default function PlanMortgage({ data, onUpdate }: PlanMortgageProps) {
  // ── Core data ─────────────────────────────────────────────────────────────
  const clientAge = data.clientAge || 0;
  const mortgageBalance = data.mortgageBalance || 0;
  const mortgageRate = data.mortgageRate || 0;
  const mortgageTermYears = data.mortgageTermYears || 0;
  const mortgageTermMonths = mortgageTermYears * 12;
  const freedomAge = data.targetFreedomAge || 55;
  const mortgagePayment = data.mortgagePayment || calcMortgagePayment(mortgageBalance, mortgageRate, mortgageTermMonths);

  // ── Tax year & dividend rate ─────────────────────────────────────────────
  const taxYear = data.taxYearOverride ?? getTaxYearForDate(data.sessionDate || new Date().toISOString());
  const rates = TAX_YEARS[taxYear];
  const dividendTaxRate = rates.dividendHigherRate; // higher rate assumed for directors

  // ── State ─────────────────────────────────────────────────────────────────
  const [overpayment, setOverpayment] = useState(data.planMortgageOverpayment || 0);
  const [annualLump, setAnnualLump] = useState(data.planMortgageAnnualLumpSum || 0);
  const [targetAge, setTargetAge] = useState(
    data.planMortgagePayoffAge && data.planMortgagePayoffAge > 0
      ? Math.round(data.planMortgagePayoffAge)
      : freedomAge
  );
  const [showLumpSum, setShowLumpSum] = useState((data.planMortgageAnnualLumpSum || 0) > 0);

  // ── Original payoff ───────────────────────────────────────────────────────
  const originalPayoffMonths = mortgagePayoffMonths(mortgageBalance, mortgageRate, mortgageTermMonths, 0, 0);
  const originalPayoffAge = clientAge + originalPayoffMonths / 12;

  // ── New payoff with overpayments ──────────────────────────────────────────
  const newPayoffMonths = mortgagePayoffMonths(mortgageBalance, mortgageRate, mortgageTermMonths, overpayment, annualLump);
  const newPayoffAge = clientAge + newPayoffMonths / 12;
  const yearsSaved = (originalPayoffMonths - newPayoffMonths) / 12;
  const interestSaved = totalInterestSaved(mortgageBalance, mortgageRate, mortgageTermMonths, overpayment, annualLump);

  // ── Cascade calculation ───────────────────────────────────────────────────
  const yearsFromPayoffToFreedom = Math.max(0, freedomAge - newPayoffAge);
  const cascadePot = yearsFromPayoffToFreedom > 0
    ? fvContributions(mortgagePayment, 6, yearsFromPayoffToFreedom)
    : 0;
  const cascadeIncome = cascadePot * 0.04;

  // ── Chart data ────────────────────────────────────────────────────────────
  const chartData = useMemo(
    () => generateMortgageChartData(mortgageBalance, mortgageRate, mortgageTermMonths, overpayment, annualLump, clientAge),
    [mortgageBalance, mortgageRate, mortgageTermMonths, overpayment, annualLump, clientAge],
  );

  // ── Sync state to ClientData ──────────────────────────────────────────────
  const syncToClientData = useCallback((op: number, lump: number) => {
    const saved = totalInterestSaved(mortgageBalance, mortgageRate, mortgageTermMonths, op, lump);
    const payoffMo = mortgagePayoffMonths(mortgageBalance, mortgageRate, mortgageTermMonths, op, lump);
    const payoffAge = clientAge + payoffMo / 12;
    const payoffDate = new Date();
    payoffDate.setFullYear(payoffDate.getFullYear() + payoffMo / 12);

    onUpdate({
      planMortgageOverpayment: op,
      planMortgageAnnualLumpSum: lump,
      planMortgagePayoffAge: Math.round(payoffAge * 10) / 10,
      planMortgagePayoffDate: payoffDate.toISOString(),
      planMortgageInterestSaved: Math.round(saved),
      totalInterestSaved: Math.round(saved),
    });
  }, [mortgageBalance, mortgageRate, mortgageTermMonths, clientAge, onUpdate]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleOverpaymentChange = (v: number) => {
    setOverpayment(v);
    // Recalculate target age from overpayment
    const mo = mortgagePayoffMonths(mortgageBalance, mortgageRate, mortgageTermMonths, v, annualLump);
    const newAge = Math.round(clientAge + mo / 12);
    setTargetAge(newAge);
    setTargetAgeInput(String(newAge));
    syncToClientData(v, annualLump);
  };

  const handleLumpSumChange = (v: number) => {
    setAnnualLump(v);
    const mo = mortgagePayoffMonths(mortgageBalance, mortgageRate, mortgageTermMonths, overpayment, v);
    const newAge = Math.round(clientAge + mo / 12);
    setTargetAge(newAge);
    setTargetAgeInput(String(newAge));
    syncToClientData(overpayment, v);
  };

  // Brief 46B S8a: allow free text entry, only recalculate on valid age
  const [targetAgeInput, setTargetAgeInput] = useState(String(targetAge));
  const handleTargetAgeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setTargetAgeInput(raw);
    const age = parseInt(raw, 10);
    if (isNaN(age) || age < clientAge + 1 || age > 85) return;
    setTargetAge(age);
    const requiredOP = overpaymentForTargetAge(mortgageBalance, mortgageRate, mortgageTermMonths, annualLump, clientAge, age);
    setOverpayment(requiredOP);
    syncToClientData(requiredOP, annualLump);
  };

  // useCommaInput hooks
  const overpaymentInput = useCommaInput(overpayment, handleOverpaymentChange, true);
  const lumpSumInput = useCommaInput(annualLump, handleLumpSumChange, true);

  // ── Sync on mount ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (overpayment > 0 || annualLump > 0) {
      syncToClientData(overpayment, annualLump);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── No-mortgage empty state ───────────────────────────────────────────────
  if (mortgageBalance === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white p-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-ma-navy mb-2">Mortgage</h1>
          <p className="text-gray-600 mb-8">No mortgage — nothing to do here.</p>
          <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center">
            <span className="text-3xl mb-3 block">✓</span>
            <p className="text-green-800 font-semibold text-lg">You're mortgage-free</p>
            <p className="text-green-700 text-sm mt-2">No mortgage balance on record. Click Next to continue.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <h1 className="text-4xl font-bold text-ma-navy mb-2">Mortgage</h1>
        <p className="text-gray-600 mb-6">Clear the mortgage early and free up cash for the next phase of the plan.</p>

        {/* Context Banner */}
        <div style={{ backgroundColor: 'rgba(100,218,210,0.1)', borderLeft: '3px solid #64DAD2', padding: '12px 16px', borderRadius: 4, fontSize: 15, fontStyle: 'italic', color: '#0B1A3E', marginBottom: 24 }}>
          Clearing the mortgage early frees up £{mortgagePayment.toLocaleString()}/month at freedom age — the fuel for the second phase of the plan.
        </div>

        {/* Current Position Panel */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8 border-l-4 border-ma-navy">
          <h2 className="text-xl font-bold text-ma-navy mb-4">Current Position</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-50 p-4 rounded">
              <p className="text-sm text-gray-600 font-medium">Mortgage balance</p>
              <p className="text-2xl font-bold text-ma-navy">{formatCurrency(mortgageBalance)}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded">
              <p className="text-sm text-gray-600 font-medium">Monthly payment</p>
              <p className="text-2xl font-bold text-ma-navy">{formatCurrency(mortgagePayment)}/mo</p>
            </div>
            <div className="bg-gray-50 p-4 rounded">
              <p className="text-sm text-gray-600 font-medium">Current mortgage-free age</p>
              <p className="text-2xl font-bold text-ma-navy">Age {Math.round(originalPayoffAge)}</p>
            </div>
          </div>
        </div>

        {/* Input Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8 border-t-4 border-ma-navy">
          <h2 className="text-xl font-bold text-ma-navy mb-6">Overpayment Options</h2>

          {/* Input 1: Target mortgage-free age */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Target mortgage-free age
            </label>
            <input
              type="number"
              value={targetAgeInput}
              onChange={handleTargetAgeChange}
              min={clientAge + 1}
              max={85}
              className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ma-navy text-lg font-semibold"
            />
            <span className="ml-2 text-sm text-gray-500">years old</span>
          </div>

          {/* Input 2: Monthly overpayment */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Monthly overpayment
            </label>
            <div className="flex items-center gap-2">
              <span className="text-lg font-semibold text-gray-900">£</span>
              <input
                {...overpaymentInput}
                placeholder="0"
                className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ma-navy"
              />
              <span className="text-gray-600">/month</span>
            </div>
          </div>

          {/* Annual lump sum — collapsed by default */}
          <div className="mb-4">
            <button
              onClick={() => setShowLumpSum(!showLumpSum)}
              className="text-sm font-semibold text-ma-navy hover:underline flex items-center gap-1"
            >
              {showLumpSum ? '▾' : '▸'} Add a lump sum
            </button>
            {showLumpSum && (
              <div className="mt-3">
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  One-off lump sum (optional)
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-semibold text-gray-900">£</span>
                  <input
                    {...lumpSumInput}
                    placeholder="0"
                    className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ma-navy"
                  />
                  <span className="text-gray-600">/year</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">e.g. bonus, retained cash in business</p>
              </div>
            )}
          </div>

          {/* 8b — Tax cost of overpayment */}
          {overpayment > 0 && (() => {
            const grossedUpMonthly = overpayment / (1 - dividendTaxRate);
            const annualDivTax = (grossedUpMonthly - overpayment) * 12;
            const yearsToPayoff = Math.max(0, Math.round(newPayoffAge) - clientAge);
            const cumulativeDivTax = annualDivTax * yearsToPayoff;
            return (
              <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-5">
                <h4 className="text-xs font-semibold text-amber-900 uppercase tracking-wide mb-3">What This Costs In Tax</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Overpayment (net)</span>
                    <span className="font-medium">{formatCurrency(overpayment)}/mo</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Gross income required ({(dividendTaxRate * 100).toFixed(2)}% div tax)</span>
                    <span className="font-medium">{formatCurrency(Math.round(grossedUpMonthly))}/mo</span>
                  </div>
                  <div className="border-t border-amber-200 pt-2 flex justify-between">
                    <span className="text-gray-700">Annual dividend tax on overpayment funds</span>
                    <span className="font-bold text-amber-700">{formatCurrency(Math.round(annualDivTax))}/yr</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">Cumulative dividend tax to age {Math.round(newPayoffAge)}</span>
                    <span className="font-bold text-amber-700">{formatCurrency(Math.round(cumulativeDivTax))}</span>
                  </div>
                </div>
                <p className="text-xs text-amber-800 mt-3">
                  This is the tax cost of funding the overpayment from dividend income. The interest saved ({formatCurrency(interestSaved)}) should be weighed against the cumulative tax cost.
                </p>
              </div>
            );
          })()}
        </div>

        {/* 8c — Rolling income allocation */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 mb-8">
          <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Your Allocation So Far</h4>
          <div className="space-y-2 text-sm">
            {(data.planDividendReduction || 0) > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">Pension commitment</span>
                <span className="font-medium">{formatCurrency(data.planDividendReduction)}/mo</span>
              </div>
            )}
            {(data.isaContribution || 0) > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">ISA contribution</span>
                <span className="font-medium">{formatCurrency(data.isaContribution)}/mo</span>
              </div>
            )}
            {(data.planDebtExtraMonthly || 0) > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">Debt extra payment</span>
                <span className="font-medium">{formatCurrency(data.planDebtExtraMonthly)}/mo</span>
              </div>
            )}
            {(data.efTopUp || 0) > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">Savings top-up</span>
                <span className="font-medium">{formatCurrency(data.efTopUp)}/mo</span>
              </div>
            )}
            {overpayment > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">Mortgage overpayment</span>
                <span className="font-medium">{formatCurrency(overpayment)}/mo</span>
              </div>
            )}
            <div className="border-t border-gray-300 pt-2 flex justify-between font-semibold">
              <span className="text-gray-700">Total committed</span>
              <span className="text-ma-navy">
                {formatCurrency(
                  (data.planDividendReduction || 0) +
                  (data.isaContribution || 0) +
                  (data.planDebtExtraMonthly || 0) +
                  (data.efTopUp || 0) +
                  overpayment
                )}/mo
              </span>
            </div>
          </div>
        </div>

        {/* Impact Panel */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-bold text-ma-navy mb-4">Impact</h2>
          <div className="space-y-4">
            <div className="flex justify-between items-center border-b border-gray-200 pb-3">
              <span className="text-gray-700">New mortgage-free age</span>
              <span className="text-3xl font-bold text-ma-navy">Age {Math.round(newPayoffAge)}</span>
            </div>
            <div className="flex justify-between items-center border-b border-gray-200 pb-3">
              <span className="text-gray-700">Years earlier than current trajectory</span>
              <span className={`font-bold text-lg ${yearsSaved > 0 ? 'text-green-600' : 'text-gray-500'}`}>
                {yearsSaved > 0 ? `${yearsSaved.toFixed(1)} years earlier` : '—'}
              </span>
            </div>
            <div className="flex justify-between items-center border-b border-gray-200 pb-3">
              <span className="text-gray-700">Total interest saved</span>
              <span className={`font-bold text-lg ${interestSaved > 0 ? 'text-green-600' : 'text-gray-500'}`}>
                {interestSaved > 0 ? formatCurrency(interestSaved) : '—'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-700">Monthly payment freed at clearance</span>
              <span className="font-bold text-lg" style={{ color: '#C9A84C' }}>
                {formatCurrency(mortgagePayment)}/month
              </span>
            </div>
          </div>
        </div>

        {/* Cascade Section — only when mortgage clears before freedom age */}
        {newPayoffAge < freedomAge && yearsFromPayoffToFreedom > 0 && (
          <div className="bg-gradient-to-r from-ma-navy to-blue-900 rounded-lg shadow-md p-6 mb-8 text-white">
            <h3 className="text-lg font-bold mb-3">Cascade Effect</h3>
            <p className="text-blue-100 mb-4">
              From age {Math.round(newPayoffAge)}, £{mortgagePayment.toLocaleString()}/month is freed.
            </p>
            <p className="text-blue-100">
              If redirected to pension for {Math.round(yearsFromPayoffToFreedom)} years:
            </p>
            <div className="mt-3 grid grid-cols-2 gap-4">
              <div className="bg-white/10 rounded-lg p-4">
                <p className="text-sm text-blue-200">Additional pot</p>
                <p className="text-2xl font-bold">{formatCurrency(cascadePot)}</p>
              </div>
              <div className="bg-white/10 rounded-lg p-4">
                <p className="text-sm text-blue-200">Additional annual income</p>
                <p className="text-2xl font-bold">{formatCurrency(cascadeIncome)}/year</p>
              </div>
            </div>
          </div>
        )}

        {/* Mortgage Balance Chart */}
        {mortgageBalance > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <h3 className="text-xl font-bold text-ma-navy mb-4">Mortgage Balance Over Time</h3>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={chartData} margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="age" tickFormatter={(age: number) => `Age ${age}`} interval={2} tick={{ fill: '#6b7280', fontSize: 12 }} />
                <YAxis tickFormatter={(v: number) => `£${(v / 1000).toFixed(0)}k`} tick={{ fill: '#6b7280', fontSize: 12 }} />
                <Tooltip
                  content={({ active, payload }: any) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0]?.payload;
                    if (!d) return null;
                    const saving = (d.standard || 0) - (d.withOverpayment || 0);
                    return (
                      <div className="bg-white border border-gray-200 rounded shadow p-3 text-sm">
                        <p className="font-bold mb-1">Age {d.age}</p>
                        <p className="text-gray-500">Standard: {formatCurrency(d.standard)}</p>
                        {overpayment > 0 && (
                          <>
                            <p className="text-ma-navy font-semibold">With overpayment: {formatCurrency(d.withOverpayment)}</p>
                            <p className="text-green-600">Saving: {formatCurrency(saving)}</p>
                          </>
                        )}
                      </div>
                    );
                  }}
                />
                <Legend wrapperStyle={{ paddingTop: '10px' }} />
                <ReferenceLine y={0} stroke="#e5e7eb" />
                <ReferenceLine
                  x={freedomAge}
                  stroke="#ef4444"
                  strokeDasharray="4 2"
                  label={{ value: `Freedom age ${freedomAge}`, position: 'top', fill: '#ef4444', fontSize: 11 }}
                />
                <Line type="monotone" dataKey="standard" stroke="#6b7280" strokeWidth={2} dot={false} name="Standard payments only" />
                <Line type="monotone" dataKey="withOverpayment" stroke="#1a2e5a" strokeWidth={3} dot={false} name={overpayment > 0 ? 'With overpayment' : 'Current trajectory'} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}
